import { useRef, useCallback } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Download, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface RoomQRCodeProps {
  roomId: string
  roomName: string
  buildingName?: string
}

/**
 * Shared helper: escapes HTML special chars to prevent XSS in print windows.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Builds the public URL for a room's display page.
 */
export function buildPublicUrl(roomId: string): string {
  return `${window.location.origin}/room/${roomId}`
}

/**
 * Generates and displays a QR code for a room's public display page.
 * Includes download (PNG) and print functionality.
 */
export function RoomQRCode({ roomId, roomName, buildingName }: RoomQRCodeProps) {
  const qrRef = useRef<HTMLDivElement>(null)

  const publicUrl = buildPublicUrl(roomId)

  const handleDownload = useCallback(() => {
    const svg = qrRef.current?.querySelector('svg')
    if (!svg) return

    // Convert SVG to canvas then to PNG
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const img = new Image()
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    img.onload = () => {
      // Add padding for the label
      const padding = 40
      const labelHeight = 80
      canvas.width = img.width + padding * 2
      canvas.height = img.height + padding * 2 + labelHeight

      // White background
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Draw QR code centered
      ctx.drawImage(img, padding, padding)

      // Add room name label below the QR code (prominent)
      ctx.fillStyle = '#000000'
      ctx.font = 'bold 20px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(roomName, canvas.width / 2, img.height + padding + 28)

      if (buildingName) {
        ctx.font = '14px sans-serif'
        ctx.fillStyle = '#666666'
        ctx.fillText(buildingName, canvas.width / 2, img.height + padding + 48)
      }

      // URL in small text
      ctx.font = '9px sans-serif'
      ctx.fillStyle = '#999999'
      ctx.fillText(publicUrl, canvas.width / 2, img.height + padding + 68)

      // Download
      const link = document.createElement('a')
      const safeName = roomName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()
      link.download = `qr-${safeName}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()

      URL.revokeObjectURL(url)
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
    }

    img.src = url
  }, [roomName, buildingName, publicUrl])

  const handlePrint = useCallback(() => {
    const svg = qrRef.current?.querySelector('svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    const safeRoomName = escapeHtml(roomName)
    const safeBuildingName = buildingName ? escapeHtml(buildingName) : ''

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>QR Code - ${safeRoomName}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            html, body {
              width: 100%;
              font-family: system-ui, sans-serif;
            }
            body {
              padding: 0.5in;
            }
            .qr-container {
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .room-name {
              font-size: 32px;
              font-weight: bold;
              margin-top: 20px;
              text-align: center;
              word-break: break-word;
            }
            .building-name {
              font-size: 18px;
              color: #666;
              margin-top: 6px;
              text-align: center;
              word-break: break-word;
            }
            .url {
              font-size: 9px;
              color: #aaa;
              margin-top: 16px;
              word-break: break-all;
              text-align: center;
            }
            @media print {
              @page { size: auto; margin: 0.5in; }
              html, body {
                height: auto;
                margin: 0;
                padding: 0;
              }
              .qr-container {
                page-break-inside: avoid;
                break-inside: avoid;
                max-height: calc(100vh - 1in);
                overflow: hidden;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            ${svgData}
            <div class="room-name">${safeRoomName}</div>
            ${safeBuildingName ? `<div class="building-name">${safeBuildingName}</div>` : ''}
            <div class="url">${escapeHtml(publicUrl)}</div>
          </div>
          <script>window.onload = function() { window.onafterprint = function() { window.close(); }; window.print(); }</script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }, [roomName, buildingName, publicUrl])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">QR Code</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center gap-4">
          <div ref={qrRef} className="rounded-lg border bg-white p-4">
            <QRCodeSVG
              value={publicUrl}
              size={200}
              level="M"
              includeMargin={false}
            />
          </div>

          <div className="text-center">
            <p className="font-semibold text-sm">{roomName}</p>
            {buildingName && (
              <p className="text-xs text-muted-foreground">{buildingName}</p>
            )}
          </div>

          <p className="text-[10px] text-muted-foreground text-center break-all max-w-[280px]">
            {publicUrl}
          </p>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="mr-2 h-4 w-4" />
              Download PNG
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
